/**
 * LoginPage — email + PIN sign-in (PR B) plus passwordless biometric
 * cold-open (PR C).
 *
 * Three sign-in surfaces:
 *   - "Sign in with biometric" button — only rendered when the device
 *     reports a platform authenticator (FaceID / fingerprint /
 *     Windows Hello) and the user has previously enrolled. Clicking
 *     it calls navigator.credentials.get with an empty allowCredentials
 *     list (discoverable credentials), so the OS picks the credential.
 *     On success the hook mints + redeems an OTP.
 *   - Email + 6-digit PIN form — the bootstrap path. Required for the
 *     first sign-in on a device.
 *   - "Forgot PIN" link — self-service PIN reset; expands an inline
 *     form whose success message is generic per no-enumeration rule.
 */

import { useState, type FormEvent, type ReactNode } from 'react'
import { Navigate, useLocation, useNavigate }       from 'react-router'
import { useTranslation }                           from 'react-i18next'
import { useSignInWithPin }       from '@/hooks/useSignInWithPin'
import { useSignInWithBiometric } from '@/hooks/useSignInWithBiometric'
import { useForgotPin }           from '@/hooks/useForgotPin'
import { useAuthStore }           from '@/store/authStore'
import { useBrand }               from '@/lib/brand/BrandProvider'
import { isAppError }             from '@/lib/errors/AppError'
import { PATHS }                  from '@/router/paths'

interface LocationState { from?: string }

export function LoginPage(): ReactNode {
  const { t }    = useTranslation()
  const brand    = useBrand()
  const session  = useAuthStore((s) => s.session)
  const loading  = useAuthStore((s) => s.loading)
  const location = useLocation()
  const navigate = useNavigate()
  const { signInWithPin, isPending, error } = useSignInWithPin()
  const biometric = useSignInWithBiometric()
  const forgot = useForgotPin()

  const initialEmail = readEmailFromQuery(location.search)
  const [email,       setEmail]       = useState(initialEmail)
  const [pin,         setPin]         = useState('')
  const [forgotOpen,  setForgotOpen]  = useState(false)
  const [forgotEmail, setForgotEmail] = useState(initialEmail)

  if (loading) return null
  if (session) {
    const dest = (location.state as LocationState | null)?.from ?? PATHS.dashboard
    return <Navigate to={dest} replace />
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    try {
      await signInWithPin(email, pin)
      const dest = (location.state as LocationState | null)?.from ?? PATHS.dashboard
      navigate(dest, { replace: true })
    } catch {
      // Error rendered from `error` below.
    }
  }

  const handleForgotSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    try {
      await forgot.requestReset(forgotEmail || email)
    } catch {
      // forgot.error renders below.
    }
  }

  const handleBiometric = async (): Promise<void> => {
    try {
      await biometric.signInWithBiometric()
      const dest = (location.state as LocationState | null)?.from ?? PATHS.dashboard
      navigate(dest, { replace: true })
    } catch {
      // biometric.error renders below.
    }
  }

  const errorMessage =
    error && isAppError(error) ? error.userMessage : null
  const biometricErrorMessage =
    biometric.error && isAppError(biometric.error) ? biometric.error.userMessage : null
  const forgotErrorMessage =
    forgot.error && isAppError(forgot.error) ? forgot.error.userMessage : null

  return (
    <main
      style={{
        minHeight:      '100vh',
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        background:     'var(--brand-neutral-50)',
        fontFamily:     'var(--brand-font-family)',
        padding:        '1rem',
      }}
    >
      <section
        style={{
          width:        '100%',
          maxWidth:     '24rem',
          background:   'var(--brand-white)',
          padding:      '1.5rem',
          borderRadius: 'var(--brand-radius-lg)',
          border:       '1px solid var(--brand-neutral-300)',
          display:      'flex',
          flexDirection:'column',
          gap:          '0.75rem',
        }}
      >
        <h1
          id="login-title"
          style={{
            margin:     0,
            fontSize:   '1.25rem',
            fontFamily: 'var(--brand-font-family-display)',
          }}
        >
          {t('auth.signInTo', { name: brand.name })}
        </h1>

        {biometric.supported && (
          <>
            <button
              type="button"
              onClick={() => void handleBiometric()}
              disabled={biometric.isPending}
              style={biometricButtonStyle(biometric.isPending)}
              aria-label={t('auth.biometricSignIn', { defaultValue: 'Sign in with biometric' })}
            >
              {biometric.isPending
                ? t('auth.biometricVerifying', { defaultValue: 'Verifying…' })
                : t('auth.biometricSignIn', { defaultValue: 'Sign in with biometric' })}
            </button>
            {biometricErrorMessage && (
              <div role="alert" style={errorPanelStyle}>
                {biometricErrorMessage}
              </div>
            )}
            <div style={dividerStyle}>
              <span style={dividerTextStyle}>
                {t('auth.orWithPin', { defaultValue: 'or sign in with PIN' })}
              </span>
            </div>
          </>
        )}

        <form onSubmit={handleSubmit} aria-labelledby="login-title" style={formStyle}>
          <label style={fieldLabelStyle}>
            <span style={fieldHintStyle}>{t('auth.emailLabel')}</span>
            <input
              type="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
            />
          </label>

          <label style={fieldLabelStyle}>
            <span style={fieldHintStyle}>{t('auth.pinLabel', { defaultValue: 'PIN' })}</span>
            <input
              type="text"
              name="pin"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoComplete="one-time-code"
              aria-describedby="pin-hint"
              required
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ''))}
              style={{ ...inputStyle, letterSpacing: '0.2em', fontSize: '1.1rem' }}
            />
            <span id="pin-hint" style={hintTextStyle}>
              {t('auth.pinHint', { defaultValue: '6-digit PIN from the welcome email.' })}
            </span>
          </label>

          {errorMessage && (
            <div role="alert" style={errorPanelStyle}>
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            disabled={isPending || pin.length !== 6}
            style={primaryButtonStyle(isPending || pin.length !== 6)}
          >
            {isPending ? t('auth.signingIn') : t('auth.signIn')}
          </button>
        </form>

        <button
          type="button"
          onClick={() => setForgotOpen((open) => !open)}
          style={linkButtonStyle}
          aria-expanded={forgotOpen}
          aria-controls="forgot-pin-form"
        >
          {forgotOpen
            ? t('auth.forgotPinClose', { defaultValue: 'Cancel' })
            : t('auth.forgotPinOpen', { defaultValue: 'Forgot PIN?' })}
        </button>

        {forgotOpen && (
          <form
            id="forgot-pin-form"
            onSubmit={handleForgotSubmit}
            aria-label="Forgot PIN"
            style={{ ...formStyle, marginTop: '0.5rem', borderTop: '1px solid var(--brand-neutral-300)', paddingTop: '0.75rem' }}
          >
            {forgot.submitted ? (
              <div role="status" style={successPanelStyle}>
                {t('auth.forgotPinSent', {
                  defaultValue: 'If that email is registered, a new PIN has been sent. Check your inbox.',
                })}
              </div>
            ) : (
              <>
                <label style={fieldLabelStyle}>
                  <span style={fieldHintStyle}>{t('auth.emailLabel')}</span>
                  <input
                    type="email"
                    name="forgot-email"
                    autoComplete="email"
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    style={inputStyle}
                  />
                </label>
                {forgotErrorMessage && (
                  <div role="alert" style={errorPanelStyle}>
                    {forgotErrorMessage}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={forgot.isPending}
                  style={secondaryButtonStyle(forgot.isPending)}
                >
                  {forgot.isPending
                    ? t('auth.forgotPinSending', { defaultValue: 'Sending…' })
                    : t('auth.forgotPinSubmit', { defaultValue: 'Send me a new PIN' })}
                </button>
              </>
            )}
          </form>
        )}
      </section>
    </main>
  )
}

function readEmailFromQuery(search: string): string {
  if (!search) return ''
  try {
    const params = new URLSearchParams(search)
    return params.get('email') ?? ''
  } catch {
    return ''
  }
}

const formStyle: React.CSSProperties = {
  display:       'flex',
  flexDirection: 'column',
  gap:           '0.75rem',
}

const fieldLabelStyle: React.CSSProperties = {
  display:       'flex',
  flexDirection: 'column',
  gap:           '0.25rem',
}

const fieldHintStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  color:    'var(--brand-neutral-700)',
}

const hintTextStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color:    'var(--brand-neutral-600)',
}

const inputStyle: React.CSSProperties = {
  padding:      '0.5rem 0.75rem',
  border:       '1px solid var(--brand-neutral-300)',
  borderRadius: 'var(--brand-radius-md)',
  fontSize:     '1rem',
}

const errorPanelStyle: React.CSSProperties = {
  background:   'var(--brand-danger-subtle)',
  color:        'var(--brand-danger)',
  border:       '1px solid var(--brand-danger)',
  borderRadius: 'var(--brand-radius-md)',
  padding:      '0.5rem 0.75rem',
  fontSize:     '0.85rem',
}

const successPanelStyle: React.CSSProperties = {
  background:   'var(--brand-success-subtle, #ecfdf5)',
  color:        'var(--brand-success, #047857)',
  border:       '1px solid var(--brand-success, #047857)',
  borderRadius: 'var(--brand-radius-md)',
  padding:      '0.5rem 0.75rem',
  fontSize:     '0.85rem',
}

function primaryButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    marginTop:    '0.5rem',
    padding:      '0.6rem 1rem',
    background:   'var(--brand-primary)',
    color:        'var(--brand-white)',
    border:       'none',
    borderRadius: 'var(--brand-radius-md)',
    fontSize:     '0.95rem',
    fontWeight:   600,
    cursor:       disabled ? 'not-allowed' : 'pointer',
    opacity:      disabled ? 0.6 : 1,
  }
}

function secondaryButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    padding:      '0.5rem 0.75rem',
    background:   'var(--brand-neutral-100)',
    color:        'var(--brand-neutral-900)',
    border:       '1px solid var(--brand-neutral-300)',
    borderRadius: 'var(--brand-radius-md)',
    fontSize:     '0.9rem',
    fontWeight:   500,
    cursor:       disabled ? 'not-allowed' : 'pointer',
    opacity:      disabled ? 0.6 : 1,
  }
}

const linkButtonStyle: React.CSSProperties = {
  background:    'transparent',
  border:        'none',
  color:         'var(--brand-primary)',
  textDecoration:'underline',
  fontSize:      '0.85rem',
  cursor:        'pointer',
  padding:       0,
  alignSelf:     'flex-start',
}

function biometricButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    padding:      '0.75rem 1rem',
    background:   'var(--brand-primary)',
    color:        'var(--brand-white)',
    border:       'none',
    borderRadius: 'var(--brand-radius-md)',
    fontSize:     '1rem',
    fontWeight:   600,
    cursor:       disabled ? 'not-allowed' : 'pointer',
    opacity:      disabled ? 0.6 : 1,
    width:        '100%',
  }
}

const dividerStyle: React.CSSProperties = {
  display:       'flex',
  alignItems:    'center',
  gap:           '0.5rem',
  margin:        '0.25rem 0',
  color:         'var(--brand-neutral-600)',
  fontSize:      '0.8rem',
}

const dividerTextStyle: React.CSSProperties = {
  flex:          '0 0 auto',
  padding:       '0 0.5rem',
  background:    'var(--brand-white)',
  position:      'relative',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}
